import { useState, useRef, Children } from 'react';


        // Per-analyst, drag-to-reorder stack for the panels on a subject/business
        // view. The chosen order is persisted in localStorage keyed per-view, so an
        // analyst's layout survives reloads. New panels shipped later (ids not yet in
        // the saved order) are appended to the end so they always appear; panels that
        // no longer exist are dropped. Native HTML5 drag-and-drop — no dependencies.
        //
        // The GRIP handle is the drag source (not the wrapper): the wrapper is the
        // drop zone. Making the whole wrapper draggable and then trying to allow only
        // grip-initiated drags does not work — Chromium reports the drag target as the
        // draggable wrapper, so a target-based guard cancels every drag. A dedicated
        // draggable handle is both reliable and keeps text selection / clicks working.
        function usePanelOrder(storageKey, ids) {
            const [order, setOrder] = useState(() => {
                let saved = [];
                try {
                    const raw = localStorage.getItem(storageKey);
                    if (raw) saved = JSON.parse(raw);
                } catch (e) { saved = []; }
                if (!Array.isArray(saved)) saved = [];
                const known = saved.filter(id => ids.includes(id));
                const appended = ids.filter(id => !known.includes(id));
                return [...known, ...appended];
            });
            const persist = (next) => {
                setOrder(next);
                try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch (e) { /* ignore */ }
            };
            return [order, persist];
        }

        // items: array of { id: string, node: ReactNode }. Callers should pass only
        // truthy items (conditional/absent panels filtered out before rendering).
        function ReorderablePanels({ storageKey, items, gap }) {
            const present = items.filter(Boolean);
            const ids = present.map(i => i.id);
            const [order, persist] = usePanelOrder(storageKey, ids);
            const [dragId, setDragId] = useState(null);
            const [overId, setOverId] = useState(null);
            const wrapRefs = useRef({});

            const byId = {};
            present.forEach(i => { byId[i.id] = i.node; });

            // Render in the saved order, dropping ids that no longer exist and
            // appending any the saved order hasn't seen yet.
            const ordered = order.filter(id => byId[id] !== undefined);
            ids.forEach(id => { if (!ordered.includes(id)) ordered.push(id); });

            // Move `sourceId` to just before `targetId` and persist.
            function reorderTo(targetId) {
                setDragId(src => {
                    if (src && src !== targetId) {
                        const next = ordered.filter(id => id !== src);
                        const idx = next.indexOf(targetId);
                        next.splice(idx < 0 ? next.length : idx, 0, src);
                        persist(next);
                    }
                    return null;
                });
                setOverId(null);
            }

            // When the host relied on a container `gap` for spacing (e.g. a flex
            // column), re-create it here since the children now live inside this
            // wrapper. Panels that carry their own margins pass no gap.
            const outerStyle = gap ? { display: 'flex', flexDirection: 'column', gap } : undefined;
            return (
                <div style={outerStyle}>
                    {ordered.map(id => {
                        const isDragging = dragId === id;
                        const isTarget = overId === id && dragId && dragId !== id;
                        return (
                            <div
                                key={id}
                                ref={el => { wrapRefs.current[id] = el; }}
                                onDragOver={(e) => {
                                    if (!dragId) return;           // ignore drags that aren't ours
                                    e.preventDefault();            // allow drop
                                    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
                                    if (overId !== id) setOverId(id);
                                }}
                                onDrop={(e) => { e.preventDefault(); reorderTo(id); }}
                                className={'reorderable' + (isDragging ? ' is-dragging' : '') + (isTarget ? ' is-drop-target' : '')}
                            >
                                <div
                                    className="reorder-grip"
                                    title="Drag to reorder this panel"
                                    aria-label="Drag to reorder this panel"
                                    draggable
                                    onClick={(e) => e.stopPropagation()}
                                    onDragStart={(e) => {
                                        setDragId(id);
                                        setOverId(null);
                                        if (e.dataTransfer) {
                                            e.dataTransfer.effectAllowed = 'move';
                                            try { e.dataTransfer.setData('text/plain', id); } catch (err) { /* firefox */ }
                                            // Drag the whole panel, not just the little handle.
                                            const el = wrapRefs.current[id];
                                            if (el && e.dataTransfer.setDragImage) {
                                                try { e.dataTransfer.setDragImage(el, 28, 24); } catch (err) { /* ignore */ }
                                            }
                                        }
                                    }}
                                    onDragEnd={() => { setDragId(null); setOverId(null); }}
                                ><span aria-hidden="true">⠿</span> Drag</div>
                                {byId[id]}
                            </div>
                        );
                    })}
                </div>
            );
        }

        // Convenience wrapper: make the direct children of a container reorderable
        // WITHOUT restructuring them into an items array. Pass `ids` parallel to the
        // children — one stable id per child, in the same order. Gate a conditionally
        // rendered child and its id with the SAME condition (falsy ids and absent
        // children are both dropped, so the two stay aligned). Lets any existing panel
        // stack become drag-to-reorder by wrapping it and listing ids.
        function ReorderableChildren({ storageKey, ids, children, gap }) {
            const kids = Children.toArray(children);
            const idList = (ids || []).filter(Boolean).map(String);
            const items = idList
                .map((id, i) => ({ id, node: kids[i] }))
                .filter(it => it.node != null);
            return <ReorderablePanels storageKey={storageKey} items={items} gap={gap} />;
        }

export { ReorderablePanels, ReorderableChildren, usePanelOrder };
